import React from 'react';
import {
  useFlow,
  Mobile,
  Desktop,
  createFeature,
} from '@edreamsodigeo/frontend-core';
import { useLogin } from '@/providers/login/LoginProvider';
import { PRIME_FIRST_PHASE_2_MOBILE } from '@/versionedComponents';
import { useIsPrimeUser } from '@/hooks/useIsPrimeUser';
import {
  OFFERS,
  PAYMENT,
  usePriceFreezeRouter,
} from '@/hooks/usePriceFreezeRouter';
import { useFlightSelection } from '@/hooks/useFlightSelection';
import { goToPFPayment } from '@/public/priceFreezeRouterUtils';
import {
  FIELDS,
  FIELDS_WITH_CONFIRM,
} from '@/components/SummaryContactDetails/formUtils';
import {
  useHomePageAction,
  useAddContactDetailsAction,
  useCreateProductAction,
} from '@/components/HomePage/useAction';
import { HomepageComponent } from '@/components/HomePage/HomepageComponent';
import { SB_ID, usePriceFreezeSession } from '@/hooks/usePriceFreezeSession';
import { useResetTMSTracking, useTrackTMSPage } from '@/hooks/useTcVars';
import { withNativeAware } from '@/HOC/withNativeAware';

const NativeVersionedMobileHomepage = createFeature({
  name: PRIME_FIRST_PHASE_2_MOBILE,
  variations: [
    HomePageRecognize,
    HomePagePrimeFirstPhase2,
    HomePagePrimeFirstPhase2,
  ],
  version: 1,
});
export const VersionedMobileHomepage = withNativeAware(
  NativeVersionedMobileHomepage,
  HomePagePrimeFirstPhase2
);

// used by both MB and DK
export function HomePageIRUM(props) {
  return (
    <>
      <Mobile>
        <HomePageIRUMMobile {...props} />
      </Mobile>
      <Desktop>
        <HomePageIRUMDesktop {...props} />
      </Desktop>
    </>
  );
}

function HomePageIRUMMobile(props) {
  useTrackTMSPage('summary');
  return <VersionedMobileHomepage {...props} />;
}

function HomePageIRUMDesktop(props) {
  return <HomePageRecognize {...props} />;
}

function HomePageRecognize({ searchId, fareItineraryKey, segmentKeys }, props) {
  // when the user clicks CONTINUE:
  // - create the SB
  // - create the PF product
  // - navigate to Payment page
  const isPrimeUser = useIsPrimeUser();
  const flow = useFlow();
  const router = usePriceFreezeRouter();
  const flightSelectionProps = useFlightSelection();
  const [getSessionItem] = usePriceFreezeSession();

  const { loading, error, onContinueClick } = useActionsToPaymentPage({
    onContinueSuccess: () => {
      if (flow === 'mobile') {
        router.navigateTo(PAYMENT);
      } else {
        const shoppingBasketId = getSessionItem(SB_ID);
        if (searchId && fareItineraryKey && segmentKeys) {
          goToPFPayment(
            searchId,
            fareItineraryKey,
            segmentKeys,
            shoppingBasketId
          );
        } else {
          goToPFPayment(
            flightSelectionProps?.searchId,
            flightSelectionProps?.fareItineraryKey,
            flightSelectionProps?.segmentKeys,
            shoppingBasketId
          );
        }
      }
    },
  });

  return (
    <HomepageComponent
      {...props}
      formFields={FIELDS}
      loading={loading}
      error={error}
      validation={() => Boolean(isPrimeUser)}
      onContinue={onContinueClick}
    />
  );
}
function HomePagePrimeFirstPhase2(props) {
  // when the user clicks CONTINUE:
  // - create the SB
  // - if the user is Prime or recognized Prime
  // - - create the price freeze product
  // - - navigate to Payment page
  // - else
  // - - add the contact details to the SB
  // - - navigate to Offers page
  const { isLogged } = useLogin();
  const isPrimeUser = useIsPrimeUser();
  useResetTMSTracking(isPrimeUser);
  const router = usePriceFreezeRouter();

  // Behavior for non logged users
  const {
    loading: loadingOffers,
    error: errorOffers,
    onContinueClick: onContinueClickToOffersPage,
  } = useActionsToOffersPage({
    onContinueSuccess: () => router.navigateTo(OFFERS),
  });
  ///////////////////////////////
  // Behaviour for prime and recognized prime users
  const {
    loading: loadingPayment,
    error: errorPayment,
    onContinueClick: onContinueClickToPaymentPage,
  } = useActionsToPaymentPage({
    onContinueSuccess: () => router.navigateTo(PAYMENT),
  });
  //

  return (
    <HomepageComponent
      {...props}
      formFields={isLogged ? FIELDS : FIELDS_WITH_CONFIRM}
      loading={loadingOffers || loadingPayment}
      error={errorOffers || errorPayment}
      onContinue={(formData) => {
        if (isPrimeUser) {
          onContinueClickToPaymentPage(formData);
        } else {
          onContinueClickToOffersPage(formData);
        }
      }}
    />
  );
}

function useActionsToOffersPage({ onContinueSuccess }) {
  // when the user clicks CONTINUE:
  // - create the SB
  // - add the contact details to the SB
  // - execute onContinueSuccess callback
  const navigationData = { formData: {} };

  const {
    loading: cdLoading,
    error: cdError,
    addContactDetails,
  } = useAddContactDetailsAction({
    onAddSuccess: () => onContinueSuccess(),
  });

  const {
    loading: sbLoading,
    error: sbError,
    onContinue: createSb,
  } = useHomePageAction({
    onContinue: (shoppingBasketId) => {
      addContactDetails(navigationData.formData, shoppingBasketId);
    },
  });

  const onContinueClick = (formData) => {
    navigationData.formData = formData;
    createSb();
  };

  return {
    loading: sbLoading || cdLoading,
    error: sbError || cdError,
    onContinueClick,
  };
}

function useActionsToPaymentPage({ onContinueSuccess }) {
  // when the user clicks CONTINUE:
  // - create the SB
  // - create the PF product
  // - execute onContinueSuccess callback
  const navigationData = { formData: {} };

  const {
    loading: pfLoading,
    error: pfError,
    createProduct,
  } = useCreateProductAction({
    onCreateSuccess: () => onContinueSuccess(),
  });

  const {
    loading: sbLoading,
    error: sbError,
    onContinue: createSb,
  } = useHomePageAction({
    onContinue: (sbId) => {
      createProduct(navigationData.formData, sbId);
    },
  });

  const onContinueClick = (formData) => {
    navigationData.formData = formData;
    createSb();
  };

  return {
    loading: sbLoading || pfLoading,
    error: sbError || pfError,
    onContinueClick,
  };
}
